/**
 * Date arithmetic for the cycles page. Everything here works on ISO date
 * strings (`YYYY-MM-DD`), which compare correctly as strings, and on whole
 * days — a cycle is a run of calendar days, never a span of milliseconds.
 *
 * The projections themselves come from the server (`CycleCard.path`); this
 * only reads them. Nothing here invents a close or a due date.
 */

import type { CycleCard, CyclePlan } from "@/lib/api/client";

const DAY_MS = 86_400_000;

export function toDay(iso: string): number {
  return Math.round(new Date(`${iso}T00:00:00Z`).getTime() / DAY_MS);
}

export function fromDay(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  return toDay(to) - toDay(from);
}

export function addDays(iso: string, days: number): string {
  return fromDay(toDay(iso) + days);
}

/** The cycle a purchase on `date` lands in, if the path reaches that far. */
export function planFor(card: CycleCard, date: string): CyclePlan | null {
  return card.path.find((p) => p.start <= date && date <= p.end) ?? null;
}

export interface FloatOn {
  card: CycleCard;
  plan: CyclePlan;
  due: string;
  days: number;
}

/** Interest-free days a purchase on `date` would get, per card, longest first. */
export function floatsOn(cards: CycleCard[], date: string): FloatOn[] {
  const out: FloatOn[] = [];
  for (const card of cards) {
    const plan = planFor(card, date);
    if (!plan?.due_on) continue;
    out.push({ card, plan, due: plan.due_on, days: daysBetween(date, plan.due_on) });
  }
  return out.sort((a, b) => b.days - a.days || a.card.last4.localeCompare(b.card.last4));
}

export type Phase = "closes-today" | "closing" | "mid" | "fresh";

/**
 * Where a card is in its cycle, in words a person plans with.
 *
 * Fresh — the first third: a purchase now has the longest wait before it is
 * billed. Closing — three days or fewer: anything bought now is on a statement
 * within days. Thresholds are presentation, not money, so they live here.
 */
export function phaseOf(card: CycleCard): Phase | null {
  if (card.days_left_in_cycle === null || card.days_into_cycle === null || !card.cycle_length_days) {
    return null;
  }
  if (card.days_left_in_cycle === 0) return "closes-today";
  if (card.days_left_in_cycle <= 3) return "closing";
  if (card.days_into_cycle / card.cycle_length_days <= 1 / 3) return "fresh";
  return "mid";
}

export const PHASE_COPY: Record<Phase, { label: string; hint: string }> = {
  "closes-today": {
    label: "Closes today",
    hint: "Anything bought today goes on the statement being drawn up right now.",
  },
  closing: {
    label: "Closing soon",
    hint: "A purchase now is billed within days — the shortest wait to pay.",
  },
  mid: {
    label: "Mid-cycle",
    hint: "A purchase now is billed at the close, then has the grace period.",
  },
  fresh: {
    label: "Fresh cycle",
    hint: "Just reset — a purchase now waits the whole cycle before it is billed.",
  },
};

export function cardName(card: CycleCard): string {
  return card.nickname || card.product_name || `${card.issuer} •••• ${card.last4}`;
}

/** Fraction of the open cycle already elapsed, 0–1. Today counts as elapsed. */
export function cycleProgress(card: CycleCard): number {
  if (!card.cycle_length_days || card.days_into_cycle === null) return 0;
  return Math.max(0, Math.min(1, card.days_into_cycle / card.cycle_length_days));
}
